import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useIntl } from 'react-intl';
import { IoIosFlag } from 'react-icons/io';
import { FancyCheckbox } from '../../../../core/components/FancyCheckbox';
import { EmployeeInitials } from '../../../../core/components/EmployeeInitials';
import { PILL_COLORS } from '../../WorkOrders/QuickTasks/constants';

export type MaintenanceKanbanCardItem = {
  id: string;
  label: string;
  subText: string;
  urgent?: boolean;
  dueDate?: string;
  status?: 'Pending' | 'Snoozed' | 'Completed' | 'InProgress';
  priority?: number;
  location?: string;
  site?: string;
  timeEstimate?: number;
  peopleNeeded?: string;
};

export interface MaintenanceKanbanCardProps {
  item: MaintenanceKanbanCardItem;
  columnId: string;
  onDropOver: (
    target: { overId: string; overColumnId: string },
    source: { srcId: string; fromColumnId: string }
  ) => void;
  onFlagClick: (columnId: string, itemId: string) => void;
  onCompleteClick: (fromColumnId: string, itemId: string) => void;
  onOpen?: (item: MaintenanceKanbanCardItem) => void;
  assigneeName?: string;
  assigneeUnassigned?: boolean;
  maxLines?: number;
}

export const KanbanCards: React.FC<MaintenanceKanbanCardProps> = ({
  item,
  columnId,
  onDropOver,
  onFlagClick,
  onCompleteClick,
  onOpen = () => {},
  assigneeName,
  assigneeUnassigned = false,
  maxLines = 2,
}) => {
  const intl = useIntl();
  const isCompleteColumn = columnId === 'col-5' || item.status === 'Completed';
  const [isChecked, setIsChecked] = useState(isCompleteColumn);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isUrgent = item.urgent === true;
  const ref = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<HTMLElement | null>(null);
  const clampStyle = useMemo(
    () => ({
      display: '-webkit-box',
      WebkitLineClamp: String(maxLines),
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word' as const,
    }),
    [maxLines]
  );

  const isOverdue = useMemo(() => {
    if (!item.dueDate) return false;
    const todayMid = new Date();
    todayMid.setHours(0, 0, 0, 0);
    let d = new Date(item.dueDate);
    if (isNaN(d.getTime())) {
      const parts = item.dueDate.split('/');
      if (parts.length === 3) {
        const m = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const y = parseInt(parts[2], 10);
        d = new Date(y, m, day);
      }
    }
    d.setHours(0, 0, 0, 0);
    return d < todayMid;
  }, [item.dueDate]);

  const priorityColor = useMemo(() => {
    const priority = item.priority ?? 0;
    const priorityKey = ['low', 'medium', 'high', 'urgent'][priority] || 'low';
    return PILL_COLORS[priorityKey]?.fg || '#0f172a';
  }, [item.priority]);

  useEffect(() => {
    setIsChecked(columnId === 'col-5' || item.status === 'Completed');
  }, [columnId, item.status]);

  const randomValue = useCallback(() => Math.floor(Math.random() * 600), []);

  const getColor = useCallback((value: number) => {
    if (value > 499) {
      return '#cf575c';
    }
    if (value > 425) {
      return '#fabb05';
    }
    return 'hsl(140deg, 75%, 45%)';
  }, []);

  const random = useMemo(() => randomValue(), [item.id, randomValue]);
  const color = getColor(random);

  const getAvatarColor = useCallback((label: string) => {
    if (!label) {
      return '#4a5568';
    }
    let hash = 0;
    for (let i = 0; i < label.length; i += 1) {
      hash = label.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 65%, 45%)`;
  }, []);

  const hasAssignee = !!assigneeName && !assigneeUnassigned;
  const avatarLabel = hasAssignee ? assigneeName! : 'U';
  const avatarStyle = hasAssignee
    ? {
        backgroundColor: getAvatarColor(assigneeName || ''),
        color: '#fff',
      }
    : {
        backgroundColor: '#e2e8f0',
        color: '#4a5568',
      };

  const locationSite = useMemo(() => {
    if (item.location && item.site) {
      return `${item.location} - ${item.site}`;
    }
    return item.location || item.site || '';
  }, [item.location, item.site]);

  const triggerProcessing = useCallback(() => {
    setIsProcessing(true);
  }, []);

  const isIpadTouch = useCallback(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /iPad/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  }, []);

  const canDrag = useCallback(
    (event: React.PointerEvent) => {
      // Allow mouse dragging
      if (event.pointerType === 'mouse') return true;
      // Allow touch dragging on mobile/tablet
      if (event.pointerType === 'touch') return true;
      // Allow iPad touch
      if (isIpadTouch()) return true;
      return false;
    },
    [isIpadTouch]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      // Don't interfere with interactive elements
      if (
        target?.closest(
          'button, a, input, select, textarea, [role="button"], .maintenance-card__checkbox-box, .maintenance-card__flag'
        )
      ) {
        return;
      }
      if (!canDrag(event)) return;

      // For touch events, prevent default to stop scrolling
      if (event.pointerType === 'touch' || isIpadTouch()) {
        event.preventDefault();
      }

      // Start tracking drag
      isDraggingRef.current = true;
      dragStartPosRef.current = { x: event.clientX, y: event.clientY };
      hasMovedRef.current = false;
    },
    [canDrag, isIpadTouch, item.id]
  );

  const isDraggingRef = useRef(false);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!isDraggingRef.current || !dragStartPosRef.current) return;

      const deltaX = Math.abs(event.clientX - dragStartPosRef.current.x);
      const deltaY = Math.abs(event.clientY - dragStartPosRef.current.y);

      if (deltaX > 5 || deltaY > 5) {
        if (!hasMovedRef.current) {
          hasMovedRef.current = true;
          setIsDragging(true);

          window.dispatchEvent(
            new CustomEvent('kanban-drag-start', {
              detail: { cardId: item.id, columnId },
            })
          );
        }

        setDragPosition({ x: event.clientX, y: event.clientY });
      }

      const pointTarget = document.elementFromPoint(event.clientX, event.clientY);
      if (event.pointerType === 'touch' || isIpadTouch()) {
        const boardEl =
          (pointTarget?.closest('.maintenance-board') as HTMLElement | null) ??
          (boardRef.current as HTMLElement | null);
        if (boardEl) {
          const rect = boardEl.getBoundingClientRect();
          const edgeThreshold = 48;
          const scrollStep = 18;
          if (event.clientX - rect.left < edgeThreshold) {
            boardEl.scrollLeft -= scrollStep;
          } else if (rect.right - event.clientX < edgeThreshold) {
            boardEl.scrollLeft += scrollStep;
          }
        }
      }
      const columnEl = pointTarget?.closest('.maintenance-column') as HTMLElement | null;

      if (columnEl) {
        const columnClass = Array.from(columnEl.classList).find((cls) =>
          cls.startsWith('maintenance-column--')
        );
        const hoveredColumnId = columnClass?.replace('maintenance-column--', '');

        if (hoveredColumnId) {
          window.dispatchEvent(
            new CustomEvent('kanban-drag-over', {
              detail: { columnId: hoveredColumnId },
            })
          );
        }
      }
    },
    [item.id, columnId, isIpadTouch]
  );

  const handlePointerUpGlobal = useCallback(
    (event: PointerEvent) => {
      if (!isDraggingRef.current) return;

      if (hasMovedRef.current) {
        const pointTarget = document.elementFromPoint(event.clientX, event.clientY);
        const columnEl = pointTarget?.closest('.maintenance-column') as HTMLElement | null;

        if (columnEl) {
          const columnClass = Array.from(columnEl.classList).find((cls) =>
            cls.startsWith('maintenance-column--')
          );
          const targetColumnId = columnClass?.replace('maintenance-column--', '');

          if (targetColumnId && targetColumnId !== columnId) {
            const columnMapping: Record<string, string> = {
              'col-1': 'Backlog',
              'col-2': 'Planning',
              'col-3': 'In Progress',
              'col-4': 'Review',
              'col-5': 'Completed',
            };

            const targetStatus = columnMapping[targetColumnId];

            if (targetStatus) {
              triggerProcessing();
              onDropOver(
                { overId: '', overColumnId: targetStatus },
                { srcId: item.id, fromColumnId: columnId }
              );
            }
          }
        }
      }

      isDraggingRef.current = false;
      dragStartPosRef.current = null;
      hasMovedRef.current = false;
      setIsDragging(false);
      setDragPosition(null);

      window.dispatchEvent(new CustomEvent('kanban-drag-end'));
    },
    [item.id, columnId, onDropOver]
  );

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUpGlobal);
    boardRef.current = document.querySelector('.maintenance-board');
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUpGlobal);
    };
  }, [handlePointerMove, handlePointerUpGlobal]);

  useEffect(() => {
    if (isProcessing) {
      setIsProcessing(false);
    }
  }, [columnId, item.status]);

  return (
    <>
      {/* Floating drag preview - rendered at document root using portal */}
      {isDragging &&
        dragPosition &&
        ReactDOM.createPortal(
          <div
            className="kanban-drag-preview"
            style={{
              position: 'fixed',
              left: dragPosition.x - 140,
              top: dragPosition.y - 52.5,
              pointerEvents: 'none',
              zIndex: 99999,
              opacity: 1,
              width: '280px',
            }}
          >
            <div
              className="maintenance-card maintenance-card--dragging-preview"
              style={{ margin: 0 }}
            >
              <div className="maintenance-card__row">
                <div className="maintenance-card__left">
                  <div className="maintenance-card__content">
                    <div className="maintenance-card__title-row">
                      <div className="maintenance-card__title">{item.label}</div>
                      <div className="maintenance-card__avatar" style={avatarStyle}>
                        <EmployeeInitials employee={avatarLabel} />
                      </div>
                    </div>
                    <div className="maintenance-card__body">
                      <div className="maintenance-card__info-column">
                        {item.dueDate && (
                          <div className="maintenance-card__estimated-date">
                            Estimated {item.dueDate}
                          </div>
                        )}
                        {locationSite && (
                          <div className="maintenance-card__location-site">{locationSite}</div>
                        )}
                        <div className="kanban-card-meta">
                          <div className="kanban-card-time">
                            <i className="uil uil-clock-three"></i>
                            <span>{item.timeEstimate ?? 0} min</span>
                          </div>
                          <div className="kanban-card-people">
                            <i className="uil uil-user"></i>
                            <span>{item.peopleNeeded ?? '1-2'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="maintenance-card__right">
                  <span className={`maintenance-card__flag ${isUrgent ? 'is-urgent' : ''}`}>
                    <IoIosFlag />
                  </span>
                </div>
              </div>
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor: priorityColor,
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                }}
              />
            </div>
          </div>,
          document.body
        )}

      <div
        ref={ref}
        className={`maintenance-card ${isDragging ? 'is-dragging' : ''} ${
          isChecked ? 'is-checked' : ''
        } ${isProcessing ? 'is-processing' : ''}`}
        style={{ opacity: isDragging ? 0.3 : isProcessing ? 0.6 : 1 }}
        onPointerDown={handlePointerDown}
        onClick={() => onOpen(item)}
      >
        {isDragging && (
          <span className="maintenance-card__badge">{intl.formatMessage({ id: 'dragging' })}</span>
        )}
        {isProcessing && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '12px',
              fontWeight: 600,
              color: '#3b5bff',
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '6px 12px',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 10,
            }}
          >
            Moving...
          </div>
        )}
        <div className="maintenance-card__row">
          <div className="maintenance-card__left">
            <FancyCheckbox
              isChecked={isChecked}
              size={28}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                if (columnId === 'col-5') {
                  return; // keep checked in Completed column
                }
                const next = !isChecked;
                setIsChecked(next);
                if (next) {
                  triggerProcessing();
                  onCompleteClick(columnId, item.id);
                }
              }}
            />
            <div className="maintenance-card__content">
              <div className="maintenance-card__title-row">
                <div className="maintenance-card__title">{item.label}</div>
                <div className="maintenance-card__avatar" style={avatarStyle}>
                  <EmployeeInitials employee={avatarLabel} />
                </div>
              </div>
              <div className="maintenance-card__body">
                <div className="maintenance-card__info-column">
                  {item.dueDate && (
                    <div className="maintenance-card__estimated-date">Estimated {item.dueDate}</div>
                  )}
                  {locationSite && (
                    <div className="maintenance-card__location-site">{locationSite}</div>
                  )}
                  <div className="kanban-card-meta">
                    <div className="kanban-card-time">
                      <i className="uil uil-clock-three" aria-hidden="true" />
                      <span>{item.timeEstimate ?? 0} min</span>
                    </div>
                    <div className="kanban-card-people">
                      <i className="uil uil-user" aria-hidden="true" />
                      <span>{item.peopleNeeded ?? '1-2'}</span>
                    </div>
                  </div>
                </div>
                <div className="maintenance-card__right">
                  <span
                    className={`maintenance-card__flag ${isUrgent ? 'is-urgent' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFlagClick(columnId, item.id);
                    }}
                    role="button"
                    aria-label={intl.formatMessage({ id: 'markUrgent' })}
                    title={intl.formatMessage({ id: 'urgent' })}
                  >
                    <IoIosFlag />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: priorityColor,
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
          }}
        />
      </div>
    </>
  );
};

export default KanbanCards;
